import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

const SELECTOR = '[data-sticky-atc]';
const instances = new WeakMap();

class StickyATC {
  /** @param {HTMLElement} section */
  constructor(section) {
    this.section = section;
    this.productId = section.dataset.productId;
    this.variantInput = section.querySelector('[data-sticky-atc-variant-input]');
    this.mediaImage = section.querySelector('[data-sticky-atc-image]');
    this.addToCartComponent = section.querySelector('add-to-cart-component');
    this.addToCartButton = this.addToCartComponent?.querySelector('button');
    this.targetSelector = section.dataset.targetSelector || '.product-information';
    this.triggerMargin = section.dataset.triggerMargin || '-80px 0px 0px 0px';
    this.abortController = new AbortController();
    this.observer = undefined;

    if (!this.productId) return;

    this.registerListeners();
    this.createObserver();
    this.section.classList.add('is-ready');
  }

  registerListeners() {
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantChange, {
      signal: this.abortController.signal,
    });
  }

  createObserver() {
    const target = document.querySelector(this.targetSelector);

    if (!target) {
      this.section.classList.add('is-visible');
      this.section.removeAttribute('aria-hidden');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          this.section.classList.remove('is-visible');
          this.section.setAttribute('aria-hidden', 'true');
        } else {
          this.section.classList.add('is-visible');
          this.section.removeAttribute('aria-hidden');
        }
      },
      { threshold: 0, rootMargin: this.triggerMargin }
    );

    this.observer.observe(target);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  handleVariantChange = (event) => {
    const variant = event.detail?.resource;
    const productId = event.detail?.data?.productId;

    if (!variant || !productId) return;
    if (productId.toString() !== this.productId) return;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    this.updateMedia(variant);
    this.updateButtonState(variant.available);

    if (this.addToCartComponent && this.mediaImage) {
      const variantMedia = variant.featured_media?.preview_image?.src || this.mediaImage.currentSrc || this.mediaImage.src;
      if (variantMedia) {
        this.addToCartComponent.dataset.productVariantMedia = variantMedia;
      }
    }

    const localEvent = new VariantUpdateEvent(variant, this.section.id, event.detail.data);
    this.section.dispatchEvent(localEvent);
  };

  /**
   * @param {boolean} isAvailable
   */
  updateButtonState(isAvailable) {
    if (!this.addToCartButton) return;
    this.addToCartButton.disabled = !isAvailable;
  }

  /**
   * @param {any} variant
   */
  updateMedia(variant) {
    if (!this.mediaImage) return;
    const previewImage = variant.featured_media?.preview_image;
    if (!previewImage?.src) return;

    this.mediaImage.src = previewImage.src;
    if (previewImage.alt) {
      this.mediaImage.alt = previewImage.alt;
    }
  }

  destroy() {
    this.abortController.abort();
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

const init = (root = document) => {
  root.querySelectorAll(SELECTOR).forEach((section) => {
    if (instances.has(section)) return;
    instances.set(section, new StickyATC(section));
  });
};

const unload = (root = document) => {
  root.querySelectorAll(SELECTOR).forEach((section) => {
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  });
};

init();

document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  unload(event.target);
});

